import { Box, Divider, Typography } from '@mui/material';
import { SECTION_COPY } from '../../content';
import { cardSurfaceSx } from '../../theme';
import type { ConditionsCardProps } from './types';

/**
 * A method's pre- and post-conditions, in one card with two labelled halves.
 *
 * **One card and not two, and the two halves stay labelled.** They are the same
 * question asked at either end — what has to be true before, what is true after
 * — so the designs bracket them together; but merging the two lists would lose
 * which is which, and that is the whole content of the section. A rule between
 * them and an overline over each keeps both readings.
 *
 * **A half with nothing in it takes no label and no rule.** Tens of pages carry
 * neither, so a card with one half is the common case rather than a degraded
 * one. The live counts are in the scheduler's `absent` report.
 */

const ConditionGroup = ({ label, items }: { label: string; items: string[] }) => (
  <Box>
    <Typography
      variant="overline"
      component="p"
      sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '1px' }}
    >
      {label}
    </Typography>

    <Box
      component="ul"
      sx={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        marginTop: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {items.map((item) => (
        <Box
          key={item}
          component="li"
          sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}
        >
          <Typography
            component="span"
            variant="body2"
            aria-hidden
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          >
            &bull;
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

export const ConditionsCard = ({ preConditions = [], postConditions = [] }: ConditionsCardProps) => {
  if (preConditions.length === 0 && postConditions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ ...cardSurfaceSx, padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Box>
        <Typography variant="h3" component="h3">
          {SECTION_COPY.conditions.heading}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: 1 }}>
          {SECTION_COPY.conditions.caption}
        </Typography>
      </Box>

      {preConditions.length > 0 && (
        <ConditionGroup label="Pre-conditions" items={preConditions} />
      )}

      {preConditions.length > 0 && postConditions.length > 0 && <Divider />}

      {postConditions.length > 0 && (
        <ConditionGroup label="Post-conditions" items={postConditions} />
      )}
    </Box>
  );
};
